import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Activity, Loader2, CheckCircle2, AlertTriangle, XCircle, Pause, Play, Square,
  RefreshCw, RotateCcw, History, Zap, User, Mail, FileText, Image as ImageIcon,
  BarChart3, BookOpen, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STAGE_PILL = {
  text:        { bg: '#eff4ff', fg: '#004ac6', label: 'Sections' },
  images:      { bg: '#ecfeff', fg: '#0891b2', label: 'Images' },
  infographic: { bg: '#f5f3ff', fg: '#7c3aed', label: 'Infographic' },
  references:  { bg: '#ecfdf5', fg: '#16a34a', label: 'References' },
  paused:      { bg: '#fef3c7', fg: '#92400e', label: 'Paused' },
  done:        { bg: '#ecfdf5', fg: '#16a34a', label: 'Done' },
};

const STATUS_PILL = {
  running:   { bg: '#eff4ff', fg: '#004ac6', label: 'Running' },
  completed: { bg: '#ecfdf5', fg: '#16a34a', label: 'Completed' },
  partial:   { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
  failed:    { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  stopped:   { bg: '#fef3c7', fg: '#92400e', label: 'Stopped' },
  paused:    { bg: '#fef3c7', fg: '#92400e', label: 'Paused' },
};

function fmtRel(iso) {
  if (!iso) return '—';
  try {
    const delta = Date.now() - new Date(iso).getTime();
    if (delta < 60_000) return 'just now';
    if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
    if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
    return `${Math.floor(delta / 86_400_000)}d ago`;
  } catch { return iso; }
}

function fmtDur(s) {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState({ active: [], count: 0 });
  const [history, setHistory] = useState({ pipeline_runs: [], automation_runs: [] });
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [confirmKill, setConfirmKill] = useState(null); // { kind, country_id, country_name, action }
  const [busyId, setBusyId] = useState(null);
  const pollRef = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/jobs`, { headers: auth() });
      setJobs(data);
      return data;
    } catch (e) {
      return null;
    } finally {
      setLoadingActive(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/jobs/history?limit=50`, { headers: auth() });
      setHistory(data);
    } catch (e) {
      /* non-fatal */
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { fetchActive(); fetchHistory(); }, [fetchActive, fetchHistory]);

  // Live polling of active jobs every 4s when there's at least one in flight,
  // every 15s otherwise (so the admin still sees newly-spawned jobs appear).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchActive();
      if (cancelled) return;
      const delay = (data?.count || 0) > 0 ? 4000 : 15000;
      pollRef.current = setTimeout(tick, delay);
    };
    tick();
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [fetchActive]);

  const action = async (job, kind /* stop|pause|resume|restart|force-stop */) => {
    setBusyId(`${job.country_id || job.kind}:${kind}`);
    try {
      let resp;
      if (job.kind === 'full_pipeline') {
        resp = await axios.post(
          `${API}/admin/jobs/full-pipeline/${job.country_id}/${kind}`,
          {},
          { headers: auth() },
        );
      } else if (job.kind === 'automation') {
        if (kind === 'stop') {
          resp = await axios.post(`${API}/admin/automation/stop`, {}, { headers: auth() });
        } else if (kind === 'pause') {
          resp = await axios.post(`${API}/admin/jobs/automation/pause`, {}, { headers: auth() });
        } else {
          toast.error(`'${kind}' isn't supported for automation`); return;
        }
      }
      toast.success(resp?.data?.detail || `${kind} queued`);
      await fetchActive();
      await fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${kind}`);
    } finally {
      setBusyId(null);
      setConfirmKill(null);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-jobs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-[#0b1c30] tracking-tight flex items-center gap-2">
          <Activity className="w-6 h-6 text-[#dc2626]" />
          Job Control
        </h1>
        <p className="text-sm text-[#434655] mt-1 max-w-2xl">
          Live admin view of every active pipeline + global automation. Stop, pause, resume or restart any run, and see who initiated it.
        </p>
      </div>

      {/* Active Jobs */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm" data-testid="active-jobs-section">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-[#dc2626]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Active Jobs</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#dc2626] bg-rose-50 px-2 py-0.5 rounded-full" data-testid="active-jobs-count">
            {jobs.count}
          </span>
          {loadingActive && <Loader2 className="w-3 h-3 animate-spin text-[#737686]" />}
          <button onClick={() => { fetchActive(); fetchHistory(); }} className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#7c3aed] hover:bg-[#f5f3ff] px-2 py-1 rounded inline-flex items-center gap-1" data-testid="refresh-jobs-btn">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {jobs.active.length === 0 ? (
          <div className="text-xs text-[#737686] italic py-4 text-center" data-testid="no-active-jobs">No pipelines or automation runs are active right now.</div>
        ) : (
          <div className="space-y-3">
            {jobs.active.map((job, i) => <JobCard key={`${job.kind}-${job.country_id || i}`} job={job} busyId={busyId} onAction={action} onConfirmKill={setConfirmKill} />)}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-[#c3c6d7]/30 p-5 shadow-sm" data-testid="job-history-section">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-[#737686]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#737686]">Recent Runs (last 50)</span>
          {loadingHistory && <Loader2 className="w-3 h-3 animate-spin text-[#737686]" />}
        </div>
        <HistoryTable label="Full Blueprint Pipelines" runs={history.pipeline_runs} kind="pipeline" />
        <div className="my-4 h-px bg-[#eff4ff]" />
        <HistoryTable label="Blueprint Automation" runs={history.automation_runs} kind="automation" />
      </div>

      {/* Confirm Kill Modal */}
      {confirmKill && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setConfirmKill(null)} data-testid="confirm-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-extrabold text-[#0b1c30] text-base">{confirmKill.title}</h3>
                <p className="text-xs text-[#434655] mt-1 leading-relaxed">{confirmKill.body}</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setConfirmKill(null)} className="px-3 py-2 rounded-lg border border-[#c3c6d7]/50 text-xs font-bold uppercase tracking-widest text-[#434655] hover:bg-[#f8f9ff]" data-testid="confirm-cancel">Cancel</button>
              <button onClick={() => action(confirmKill.job, confirmKill.action)} className="px-3 py-2 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] text-white text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="confirm-execute">
                {confirmKill.confirmIcon || <Square className="w-3 h-3" />}
                {confirmKill.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InitiatorBadge({ initiator }) {
  const label = initiator?.full_name || initiator?.email || 'system';
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#434655] bg-[#f8f9ff] border border-[#eff4ff] rounded-full px-2 py-0.5 max-w-[180px] truncate" title={initiator?.email || ''}>
      <User className="w-3 h-3" /> {label}
    </span>
  );
}

function JobCard({ job, busyId, onAction, onConfirmKill }) {
  const isFullPipeline = job.kind === 'full_pipeline';
  const stage = job.stage || (job.kind === 'automation' ? 'text' : null);
  const pill = STAGE_PILL[stage] || { bg: '#f1f5f9', fg: '#0b1c30', label: stage || '—' };
  const total = isFullPipeline ? job.total_steps || 0 : 100;
  const done = isFullPipeline ? job.steps_done || 0 : job.progress_pct || 0;
  const pct = isFullPipeline && total > 0 ? Math.round((done / total) * 100) : (job.progress_pct || 0);
  const label = isFullPipeline ? job.current_label : job.current_step;
  const isPaused = stage === 'paused' || job.paused;
  const busyKey = (k) => busyId === `${job.country_id || job.kind}:${k}`;

  return (
    <div className="p-4 rounded-lg bg-gradient-to-br from-[#f8f9ff] to-white border border-[#eff4ff]" data-testid={`job-card-${job.country_id || job.kind}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-[10px] font-black uppercase tracking-widest text-[#7c3aed] bg-[#f5f3ff] px-2 py-0.5 rounded-full">
            {isFullPipeline ? 'Pipeline' : 'Automation'}
          </span>
          <span className="font-extrabold text-[#0b1c30] text-base truncate" data-testid={`job-country-${job.country_id || job.kind}`}>
            {job.country_name || '—'}
          </span>
          {job.sector && <span className="text-[10px] text-[#737686]">· {job.sector}</span>}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: pill.bg, color: pill.fg }}>
            {isPaused ? <Pause className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
            {pill.label}
          </span>
        </div>
        <InitiatorBadge initiator={job.initiator} />
      </div>

      <div className="text-xs text-[#434655] truncate mb-2" title={label || ''} data-testid={`job-label-${job.country_id || job.kind}`}>{label || '—'}</div>

      <div className="h-1.5 rounded-full bg-[#eff4ff] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#004ac6] via-[#0891b2] to-[#7c3aed] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[#737686] mt-1.5 mb-3">
        <span>{isFullPipeline ? `${done} / ${total} steps` : `${pct}%`}</span>
        <span>started {fmtRel(job.started_at)}</span>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2">
        {isFullPipeline && !isPaused && (
          <ActionBtn icon={Pause} label="Pause" busy={busyKey('pause')} onClick={() => onAction(job, 'pause')} testid={`pause-${job.country_id}`} variant="amber" />
        )}
        {isFullPipeline && isPaused && (
          <ActionBtn icon={Play} label="Resume" busy={busyKey('resume')} onClick={() => onAction(job, 'resume')} testid={`resume-${job.country_id}`} variant="green" />
        )}
        <ActionBtn
          icon={Square}
          label="Stop"
          busy={busyKey('stop')}
          onClick={() => onConfirmKill({
            job, action: 'stop', title: `Stop ${job.country_name}?`,
            body: 'Pipeline will exit after the current step completes. Progress so far is preserved. You can re-launch from Report Builder.',
            confirmLabel: 'Stop pipeline', confirmIcon: <Square className="w-3 h-3" />,
          })}
          testid={`stop-${job.country_id || job.kind}`}
          variant="red"
        />
        {isFullPipeline && (
          <ActionBtn
            icon={RotateCcw}
            label="Restart"
            busy={busyKey('restart')}
            onClick={() => onConfirmKill({
              job, action: 'restart', title: `Restart ${job.country_name}?`,
              body: 'Pipeline will stop, then re-launch from the beginning with the current "force regenerate" setting (off). All sections already generated will be SKIPPED — they\'ll be regenerated only if you flip force-regenerate on first.',
              confirmLabel: 'Restart pipeline', confirmIcon: <RotateCcw className="w-3 h-3" />,
            })}
            testid={`restart-${job.country_id}`}
            variant="violet"
          />
        )}
        {isFullPipeline && (
          <ActionBtn
            icon={Zap}
            label="Force kill"
            busy={busyKey('force-stop')}
            onClick={() => onConfirmKill({
              job, action: 'force-stop', title: `Force-kill ${job.country_name}?`,
              body: 'Immediately marks the pipeline as stopped — bypasses the graceful exit wait. Use this only when an orchestrator is unresponsive (server restart left an orphan record).',
              confirmLabel: 'Force-kill', confirmIcon: <Zap className="w-3 h-3" />,
            })}
            testid={`force-stop-${job.country_id}`}
            variant="red"
          />
        )}
      </div>

      {/* Inline error preview */}
      {(job.errors && job.errors.length > 0) && (
        <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[10px] font-mono text-amber-900 max-h-24 overflow-y-auto">
          <div className="flex items-center gap-1 font-bold mb-1 uppercase tracking-widest">
            <AlertTriangle className="w-3 h-3" /> {job.errors.length} skipped / failed
          </div>
          {job.errors.slice(-4).map((e, i) => <div key={i} className="truncate">{e}</div>)}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, busy, onClick, testid, variant = 'red' }) {
  const styles = {
    red:    'bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-200',
    amber:  'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200',
    green:  'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200',
    violet: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border-violet-200',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]}`}
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {busy ? 'working…' : label}
    </button>
  );
}

function HistoryTable({ label, runs, kind }) {
  if (!runs || runs.length === 0) {
    return (
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-[#737686] mb-2">{label}</div>
        <div className="text-xs text-[#737686] italic py-2">No runs yet.</div>
      </div>
    );
  }
  return (
    <div data-testid={`history-${kind}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-[#737686] mb-2">{label} ({runs.length})</div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] font-black uppercase tracking-widest text-[#737686] border-b border-[#eff4ff]">
              <th className="px-2 py-2">Country</th>
              <th className="px-2 py-2">Initiator</th>
              <th className="px-2 py-2">Status</th>
              {kind === 'pipeline' && <th className="px-2 py-2"><FileText className="inline w-3 h-3" /> Text</th>}
              {kind === 'pipeline' && <th className="px-2 py-2"><ImageIcon className="inline w-3 h-3" /> Img</th>}
              {kind === 'pipeline' && <th className="px-2 py-2"><BarChart3 className="inline w-3 h-3" /> Info</th>}
              {kind === 'pipeline' && <th className="px-2 py-2"><BookOpen className="inline w-3 h-3" /> Ref</th>}
              {kind === 'automation' && <th className="px-2 py-2">Sections</th>}
              <th className="px-2 py-2"><Clock className="inline w-3 h-3" /> Elapsed</th>
              <th className="px-2 py-2">Finished</th>
            </tr>
          </thead>
          <tbody>
            {runs.slice(0, 20).map((r) => {
              const pill = STATUS_PILL[r.status] || { bg: '#f1f5f9', fg: '#0b1c30', label: r.status || '—' };
              return (
                <tr key={r.id} className="border-b border-[#f8f9ff] hover:bg-[#f8f9ff]/50">
                  <td className="px-2 py-2 font-bold text-[#0b1c30]">{r.country_name || '—'}</td>
                  <td className="px-2 py-2"><InitiatorBadge initiator={r.initiator} /></td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: pill.bg, color: pill.fg }}>
                      {r.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                      {r.status === 'failed' && <XCircle className="w-3 h-3" />}
                      {(r.status === 'partial' || r.status === 'stopped' || r.status === 'paused') && <AlertTriangle className="w-3 h-3" />}
                      {pill.label}
                    </span>
                  </td>
                  {kind === 'pipeline' && <td className="px-2 py-2">{r.text_done_count ?? '—'}/{r.tier_keys_count ?? '—'}</td>}
                  {kind === 'pipeline' && <td className="px-2 py-2">{r.images_done_count ?? '—'}</td>}
                  {kind === 'pipeline' && <td className="px-2 py-2">{r.infographic_done ? '✓' : '—'}</td>}
                  {kind === 'pipeline' && <td className="px-2 py-2">{r.references_done ? '✓' : '—'}</td>}
                  {kind === 'automation' && <td className="px-2 py-2">{r.sections_done_count ?? '—'}</td>}
                  <td className="px-2 py-2 font-mono">{fmtDur(r.duration_seconds)}</td>
                  <td className="px-2 py-2 text-[#737686]">{fmtRel(r.finished_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
